import moment from 'jalali-moment';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from 'zaman';
import { Sidebar } from '../Layout/Sidebar';

export interface DateRange { from: string; to: string }

const today = () => moment().format('jYYYY/jMM/jDD');

export const DATE_ALL = '';

// Prices/income are stored and returned by the backend in Rial; every
// analytics page displays them labeled "تومان" (Toman = 10 Rial), so convert
// once here rather than at the API layer (order pages etc. still want Rial).
export const toToman = (rial: number | undefined | null) => (rial || 0) / 10;

// Exactly these 4, same everywhere /analytics is used — no custom date range
// picker anymore. "سال مالی <جاری>"/"سال مالی <قبلی>" are computed from the
// current Jalali year at click time, so these stay correct without yearly upkeep.
const currentJalaliYear = () => moment().format('jYYYY');
const lastJalaliYear = () => moment().subtract(1, 'jYear').format('jYYYY');

const PRESETS = [
  { label: 'همه', from: () => DATE_ALL, to: () => DATE_ALL },
  { label: `سال مالی ${currentJalaliYear()}`, from: () => moment().startOf('jYear').format('jYYYY/jMM/jDD'), to: () => today() },
  { label: `سال مالی ${lastJalaliYear()}`, from: () => moment().subtract(1, 'jYear').startOf('jYear').format('jYYYY/jMM/jDD'), to: () => moment().subtract(1, 'jYear').endOf('jYear').format('jYYYY/jMM/jDD') },
  { label: 'سی روز گذشته', from: () => moment().subtract(30, 'day').format('jYYYY/jMM/jDD'), to: () => today() },
];

// Same presets, same markup, reused by the per-item detail pages
// (ProductDetail/UserDetail) so the date filter is genuinely identical
// everywhere in /analytics — those pages used to have their own leftover
// custom date-range picker + "اعمال" button.
//
// "بازه دلخواه" is a 5th, non-computed option — active whenever the current
// dateRange doesn't match any of the 4 fixed presets — which reveals a pair
// of از/تا pickers instead of jumping straight to a new range.
export function DateRangePresets({ dateRange, onChange }: { dateRange: DateRange; onChange: (dr: DateRange) => void }) {
  const activePresetIndex = PRESETS.findIndex(p => p.from() === dateRange.from && p.to() === dateRange.to);
  const isCustom = activePresetIndex === -1;

  return (
    <div className="analyticsFilterPresets" style={{ flexWrap: 'wrap' }}>
      {PRESETS.map((p, i) => (
        <button
          key={i}
          className={`presetBtn${activePresetIndex === i ? ' active' : ''}`}
          onClick={() => onChange({ from: p.from(), to: p.to() })}
        >
          {p.label}
        </button>
      ))}
      <button
        className={`presetBtn${isCustom ? ' active' : ''}`}
        onClick={() => { if (!isCustom) onChange({ from: today(), to: today() }); }}
      >
        بازه دلخواه
      </button>
      {isCustom && (
        <>
          <span className="analyticsFilterLabel">از:</span>
          <DatePicker
            inputClass="editProductInput"
            defaultValue={dateRange.from ? moment(dateRange.from, 'jYYYY/jMM/jDD').toDate() : new Date()}
            onChange={e => onChange({ ...dateRange, from: moment(e.value.valueOf()).format('jYYYY/jMM/jDD') })}
          />
          <span className="analyticsFilterLabel">تا:</span>
          <DatePicker
            inputClass="editProductInput"
            defaultValue={dateRange.to ? moment(dateRange.to, 'jYYYY/jMM/jDD').toDate() : new Date()}
            onChange={e => onChange({ ...dateRange, to: moment(e.value.valueOf()).format('jYYYY/jMM/jDD') })}
          />
        </>
      )}
    </div>
  );
}

type Tab = 'overview' | 'users' | 'orders' | 'products';

interface Props {
  tab: Tab;
  dateRange: DateRange;
  onDateChange: (dr: DateRange) => void;
  children: React.ReactNode;
  title: string;
}

const TAB_LABELS: Record<Tab, string> = {
  overview: 'خلاصه کلی',
  users: 'کاربران',
  orders: 'سفارشات',
  products: 'محصولات',
};

const TAB_ICONS: Record<Tab, string> = {
  overview: '📊',
  users: '👥',
  orders: '📦',
  products: '🏷️',
};

export default function AnalyticsLayout({ tab, dateRange, onDateChange, children, title }: Props) {
  const navigate = useNavigate();

  return (
    <body className="dashboardBody">
      <Sidebar />
      <main className="analyticsMain">
        {/* Top header */}
        <div className="analyticsHeader">
          <h1 className="analyticsTitle">{title}</h1>
        </div>

        {/* Tab navigation */}
        <div className="analyticsTabs">
          {(Object.keys(TAB_LABELS) as Tab[]).map(t => (
            <button
              key={t}
              className={`analyticsTab${tab === t ? ' active' : ''}`}
              onClick={() => navigate(`/analytics${t === 'overview' ? '' : '/' + t}`)}
            >
              <span>{TAB_ICONS[t]}</span>
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>

        {/* Date range filter — click applies immediately, no separate "apply" step */}
        <div className="analyticsFilters">
          <DateRangePresets dateRange={dateRange} onChange={onDateChange} />
        </div>

        {children}
      </main>
    </body>
  );
}
